import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, User } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import { filterPractitionerMembers, withStickySelectedMembers } from '@/lib/practitionerRole';
import {
  useEligiblePractitionersForCourseStep,
  useTreatmentCourseStepSlots,
  type TreatmentCourseStepSlot,
} from '@/hooks/useBookTreatmentCourse';
import {
  addCivilWeeks,
  isCivilInCourseWindow,
  mondayOfCivil,
  weekCivils,
  type CourseIntervalWindow,
} from '@/lib/courseIntervalWindow';
import {
  formatCourseCivilDayShort,
  formatCourseTime,
  formatCourseWeekSpan,
} from '@/lib/courseDateFormat';
import {
  COURSE_SLOT_FILTER_ALL,
  courseSlotChipAriaLabel,
  courseSlotChipShowsPractitioner,
  courseSlotSearchPractitionerIds,
  defaultCourseSlotPractitionerFilter,
  isCourseSlotChipSelected,
  groupSlotsByCivilDay,
  practitionerDisplayName,
  resolvePractitionerLabel,
  restrictCourseSlotPractitionerOptions,
  slotPrimaryPractitionerId,
} from '@/lib/courseStepSlotPicker';
import { initialsFromName } from '@/lib/initials';
import {
  clampIsoToNow,
  civilDateFromIso,
  futureSlots,
  localDayEndIso,
  localDayStartIso,
  roundNowMs,
} from '@/lib/treatmentCourseWizard';
import { isoWeekNumber } from '@/utils/appointmentTimeFormat';
import { cn } from '@/utils/cn';

const VISIBLE_SLOTS = 8;

export function CourseSlotWeekPicker({
  clinicId,
  bookedStepId,
  previousStartsAt,
  window,
  selectedStartsAt,
  selectedPractitionerIds,
  currentStartsAt,
  assignedPractitionerIds,
  onSelectSlot,
  enabled,
}: {
  clinicId: string;
  bookedStepId: string;
  previousStartsAt?: string | null;
  window: CourseIntervalWindow | null;
  selectedStartsAt?: string | null;
  selectedPractitionerIds?: string[];
  currentStartsAt?: string | null;
  assignedPractitionerIds: string[];
  onSelectSlot: (slot: TreatmentCourseStepSlot) => void;
  enabled: boolean;
}) {
  const { t, i18n } = useTranslation();
  const fallbackName = t('treatment_courses.practitioner_fallback');
  const { data: members } = useClinicMembers(clinicId);
  const { data: roles, isSuccess: rolesReady } = useClinicRoles(clinicId);
  const { data: eligibleIds } = useEligiblePractitionersForCourseStep(bookedStepId, enabled);
  const hasWindow = window != null;
  const initialMonday = mondayOfCivil(
    civilDateFromIso(selectedStartsAt ?? currentStartsAt ?? null) ??
      window?.centerCivil ??
      civilDateFromIso(new Date().toISOString()) ??
      '',
  );
  const [monday, setMonday] = useState(initialMonday);
  const [filterId, setFilterId] = useState(() =>
    defaultCourseSlotPractitionerFilter(assignedPractitionerIds),
  );
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const friday = weekCivils(monday, 5)[4] ?? monday;
  const sunday = weekCivils(monday, 7)[6] ?? friday;
  const searchNowMs = useMemo(() => roundNowMs(), [monday]);
  const slotInput = useMemo(
    () =>
      enabled
        ? {
            clinicId,
            bookedStepId,
            windowStart: clampIsoToNow(localDayStartIso(monday), searchNowMs),
            windowEnd: localDayEndIso(sunday),
            previousStartsAt: previousStartsAt ?? null,
            practitionerIds: courseSlotSearchPractitionerIds(filterId),
          }
        : null,
    [bookedStepId, clinicId, enabled, filterId, monday, previousStartsAt, searchNowMs, sunday],
  );
  const { data: slots, isLoading } = useTreatmentCourseStepSlots(slotInput, Boolean(slotInput));
  const visibleSlots = useMemo(() => futureSlots(slots ?? []), [slots]);
  const grouped = useMemo(() => groupSlotsByCivilDay(visibleSlots), [visibleSlots]);
  const days = weekCivils(monday, 5);
  const currentCivil = civilDateFromIso(currentStartsAt ?? null);
  const currentTime = formatCourseTime(currentStartsAt);
  const weekStartDate = new Date(`${monday}T12:00:00`);
  const weekLabel = Number.isNaN(weekStartDate.getTime())
    ? ''
    : t('treatment_courses.week_n', { week: isoWeekNumber(weekStartDate) });
  const showPractitioner = courseSlotChipShowsPractitioner(filterId);

  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members ?? []) {
      const label = practitionerDisplayName(member.name || member.email, fallbackName);
      if (label !== fallbackName) map.set(member.user_id, label);
    }
    return map;
  }, [fallbackName, members]);

  const filterOptions = useMemo(() => {
    const all = members ?? [];
    const asOptions = (rows: typeof all) =>
      rows.map((member) => ({
        id: member.user_id,
        name: resolvePractitionerLabel(member.user_id, names, fallbackName),
      }));
    if (eligibleIds != null) {
      return restrictCourseSlotPractitionerOptions(asOptions(all), eligibleIds);
    }
    if (!rolesReady) return asOptions(all);
    const flagged = withStickySelectedMembers(filterPractitionerMembers(all, roles ?? []), all, [
      filterId,
      ...(assignedPractitionerIds ?? []),
      ...(selectedPractitionerIds ?? []),
    ]);
    return asOptions(flagged);
  }, [
    assignedPractitionerIds,
    eligibleIds,
    fallbackName,
    filterId,
    members,
    names,
    roles,
    rolesReady,
    selectedPractitionerIds,
  ]);

  return (
    <div data-testid="course-slot-week-picker">
      <div className="pk-tools">
        <div className="wk">
          <Button
            type="button"
            variant="secondary"
            size="icon"
            aria-label={t('appointments.prev_week')}
            onClick={() => setMonday((current) => addCivilWeeks(current, -1))}
          >
            <ChevronLeft className="i sm" />
          </Button>
          <div className="wl num">
            {weekLabel}
            <small>
              {formatCourseWeekSpan(`${monday}T12:00:00`, `${friday}T12:00:00`, i18n.language)}
            </small>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            aria-label={t('appointments.next_week')}
            onClick={() => setMonday((current) => addCivilWeeks(current, 1))}
          >
            <ChevronRight className="i sm" />
          </Button>
        </div>
        <div className="w-[220px]">
          <Select
            value={filterId}
            aria-label={t('appointments.filter_practitioner_all')}
            onChange={(event) => setFilterId(event.target.value)}
            leadingIcon={<User className="i sm" aria-hidden />}
          >
            <option value={COURSE_SLOT_FILTER_ALL}>
              {t('appointments.filter_practitioner_all')}
            </option>
            {filterOptions.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </Select>
        </div>
        {hasWindow ? (
          <div className="r">
            <span className="lgd">
              <i />
              {t('treatment_courses.in_window')}
            </span>
            <span className="lgd">
              <i className="o" />
              {t('treatment_courses.out_of_window')}
            </span>
          </div>
        ) : null}
      </div>

      {isLoading ? <p className="hint">{t('treatment_courses.book_slots_loading')}</p> : null}

      <div className="days">
        {days.map((civil) => {
          const inWindow = hasWindow && isCivilInCourseWindow(civil, window);
          const daySlots = grouped.get(civil) ?? [];
          const open = expanded[civil] === true;
          const shown = open ? daySlots : daySlots.slice(0, VISIBLE_SLOTS);
          const extra = daySlots.length - shown.length;
          return (
            <div key={civil} className={cn('day', inWindow && 'inw')}>
              <div className="dh">
                <b className="num">{formatCourseCivilDayShort(civil, i18n.language)}</b>
                {hasWindow ? (
                  <span>
                    {inWindow
                      ? t('treatment_courses.in_window')
                      : t('treatment_courses.out_of_window')}
                  </span>
                ) : null}
              </div>
              {daySlots.length === 0 && !isLoading ? (
                <p className="none">{t('treatment_courses.book_slots_empty')}</p>
              ) : (
                <div className="slots num">
                  {shown.map((slot) => {
                    const selected = isCourseSlotChipSelected(slot, {
                      startsAt: selectedStartsAt,
                      practitionerIds: selectedPractitionerIds,
                    });
                    const was =
                      currentCivil === civil && formatCourseTime(slot.starts_at) === currentTime;
                    const slotOut =
                      hasWindow && !isCivilInCourseWindow(civilDateFromIso(slot.starts_at), window);
                    const practitionerId = slotPrimaryPractitionerId(slot);
                    const practitionerName = practitionerId
                      ? resolvePractitionerLabel(practitionerId, names, fallbackName)
                      : fallbackName;
                    const time = formatCourseTime(slot.starts_at);
                    return (
                      <button
                        key={`${slot.starts_at}-${slot.practitioner_ids.join('-')}`}
                        type="button"
                        aria-pressed={selected}
                        aria-label={courseSlotChipAriaLabel(
                          time,
                          practitionerName,
                          showPractitioner,
                        )}
                        title={showPractitioner ? practitionerName : undefined}
                        onClick={() => onSelectSlot(slot)}
                        className={cn(
                          'slot',
                          selected && !slotOut && 'sel',
                          selected && slotOut && 'selbad',
                          !selected && was && 'was',
                        )}
                      >
                        {time}
                        {showPractitioner ? (
                          <span aria-hidden>
                            {initialsFromName(practitionerName) || practitionerName.slice(0, 2)}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                  {extra > 0 ? (
                    <button
                      type="button"
                      className="slmore"
                      onClick={() => setExpanded((current) => ({ ...current, [civil]: true }))}
                    >
                      {t('treatment_courses.more_slots', { count: extra })}
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
